/**
 * De bibliotheek van één model (`/content?model=<id>`).
 *
 * Gemeten aan het oude scherm (IvySkyeXx, 5691 items, viewport 1920×1080):
 *
 *   mappenboom   x=280 w= 260 h=800   kop "Folders", boom met tellers
 *   werkbalk     x=556 w=1324 h=117   status, mediasoort, zoeken, acties
 *   mediagrid    x=556 w=1324         zes kolommen van 211×263
 *
 * De tellers komen van de server en worden hier niet nagerekend: "new" is
 * precies wat oud "Unprocessed" noemt — media die nog niet verwerkt zijn.
 * Dat is ook waarom bij de meeste modellen álles op new staat: er is nog
 * niets verwerkt (Processed = 0).
 *
 * Miniaturen komen uit de servercache (`/api/admin/content/thumbnail/{id}`),
 * die ze één keer aanmaakt vanaf de NAS — ook een frame uit een video. Ze
 * laden lui: een kaart die nog niet in beeld is haalt niets op, anders staat
 * één model met 7000 foto's de hele NAS leeg te trekken.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { ErrorNote, Loading, Seg, Tag } from '../components/ui'
import { useApi } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { Modal } from '../components/Modal'
import { api, fileUrl, thumbUrl } from '../lib/api'
import { isVolledigBeheer, useMij } from '../lib/rechten'
import { ContentBewerken } from '../components/ContentBewerken'
import type { MapKeuze } from '../components/ContentBewerken'
import { num } from '../lib/format'
import { useBijRand, useInBeeld } from '../lib/scrollLaden'

export interface Map_ {
  id: number
  parent_id: number | null
  name: string
  folder_type: string
  item_count: number
  unprocessed_count: number
  hidden_from_chat: boolean
  /** 23-10-2026: zichtbaar en verkoopbaar voor chatters (per map). */
  chatter_visible?: boolean
  /** 26-10-2026: de andere doelen van de map (alleen de owner zet ze). */
  posting_bot_visible?: boolean
  paid_bot_visible?: boolean
  social_visible?: boolean
  children?: Map_[]
}

export interface Item {
  id: number
  folder_id: number | null
  folder_name: string | null
  folder_path: string | null
  processing_status: string
  title: string
  content_type: string
  intensity: string | null
  tags: string[]
  file_reference: string | null
  duration_seconds?: number | null
  file_size?: number | null
  created_at?: string | null
  description?: string | null
  status?: string | null
  base_price?: number | null
  min_price?: number | null
  max_price?: number | null
  progression_order?: number | null
}

/** Wat een admin met één kaart kan (23-10-2026, de controls van oud). */
interface KaartActies {
  download: (item: Item) => void
  converteer: (item: Item) => void
  verwerk: (item: Item) => void
  terug: (item: Item) => void
  bewerk: (item: Item) => void
  archiveer: (item: Item) => void
}

/** Bestanden die geen JPG/MP4 zijn; die markeert oud met een gekleurde knop. */
const MOET_OM = /\.(heic|heif|png|webp|avif|bmp|gif|mov|avi|wmv|mkv|webm|m4v)$/i

/** De boom als platte lijst met volledige paden, voor "verplaats naar map". */
function plat(mappen: Map_[], voor = ''): MapKeuze[] {
  return mappen.flatMap((m) => {
    const pad = voor ? `${voor} / ${m.name}` : m.name
    return [{ id: m.id, pad }, ...plat(m.children ?? [], pad)]
  })
}

const SOORT = [
  { value: 'all', label: 'All media' },
  { value: 'photo', label: 'Photos' },
  { value: 'video', label: 'Videos' },
  { value: 'audio', label: 'Audio' },
]

const PER_PAGINA = 60

/** Wat een synchronisatie zou veranderen; komt van de leesroute. */
interface Proef {
  model: string
  nas_root: string | null
  on_nas: number
  in_index: number
  new_on_nas: string[]
  new_on_nas_total?: number
  missing_on_nas: string[]
  missing_on_nas_total?: number
  message?: string
}

/** Een tak in de mappenboom, met zijn eigen inklapstand. */
function Tak({ map, actief, onKies, diep, onZichtbaar, onDoelen }: {
  map: Map_
  actief: number | null
  /** 26-10-2026: alleen voor de owner, opent "Folder purposes". */
  onDoelen?: (map: Map_) => void
  onKies: (id: number | null) => void
  diep: number
  /** Alleen voor owner/admin: het vinkje "zichtbaar voor chatters". */
  onZichtbaar?: (map: Map_, aan: boolean) => void
}) {
  // Alleen de bovenste laag staat open. Een boom die helemaal openklapt is bij
  // IvySkyeXx zeven schermen lang en dan is de eerste map niet meer te vinden.
  const [open, setOpen] = useState(diep === 0)
  const kinderen = map.children ?? []
  return (
    <>
      <div className={actief === map.id ? 'tak op' : 'tak'}
           style={{ paddingLeft: 8 + diep * 14 }}>
        {kinderen.length ? (
          <button type="button" className="klap" onClick={() => setOpen((v) => !v)}
                  aria-label={open ? 'Collapse' : 'Expand'}>
            {open ? '▾' : '▸'}
          </button>
        ) : <span className="klap leeg" />}
        <button type="button" className="naam" onClick={() => onKies(map.id)}>
          {map.name}
        </button>
        <span className="mut2 telling">{num(map.item_count)}</span>
        {onZichtbaar ? (
          <input type="checkbox" className="chattervink" checked={!!map.chatter_visible}
                 title={map.chatter_visible
                   ? 'Visible and sellable for chatters. Click to hide.'
                   : 'Hidden from chatters. Click to make visible and sellable in the chats.'}
                 onChange={(e) => onZichtbaar(map, e.target.checked)} />
        ) : null}
        {onDoelen ? (
          <button type="button" className="btn ghost sm" title="Folder purposes: chatters, posting bot, paid bot, social media"
                  aria-label="Folder purposes" onClick={() => onDoelen(map)}
                  style={{ padding: '0 5px', fontSize: 11 }}>⚙</button>
        ) : null}
      </div>
      {open ? kinderen.map((k) => (
        <Tak key={k.id} map={k} actief={actief} onKies={onKies} diep={diep + 1}
             onZichtbaar={onZichtbaar} onDoelen={onDoelen} />
      )) : null}
    </>
  )
}

/** Eén mediakaart. Het plaatje laadt pas als de kaart in beeld komt. */
function Kaart({ item, gekozen, onKies, onOpen, scrollvak, acties }: {
  item: Item
  gekozen: boolean
  onKies: (id: number, aan: boolean) => void
  onOpen: (item: Item) => void
  scrollvak: RefObject<HTMLDivElement | null>
  /** Alleen voor owner/admin; zonder blijft de kaart zoals hij was. */
  acties?: KaartActies
}) {
  const el = useRef<HTMLDivElement | null>(null)
  const [stuk, setStuk] = useState(false)
  // 300px marge vanaf het SCROLLVAK (niet het venster): het plaatje is er
  // meestal al voordat de kaart in beeld schuift, zonder dat de hele lijst
  // tegelijk laadt. Zie lib/scrollLaden.ts.
  const zichtbaar = useInBeeld(el, scrollvak, '300px 0px')

  const duur = item.duration_seconds
    ? `${Math.floor(item.duration_seconds / 60)}:${String(
        Math.round(item.duration_seconds % 60)).padStart(2, '0')}`
    : null

  return (
    <div className={gekozen ? 'mediakaart aan' : 'mediakaart'} ref={el}>
      <div className="beeld" onClick={() => onOpen(item)}>
        {zichtbaar && !stuk ? (
          <img src={thumbUrl(item.id)} alt={item.title} loading="lazy"
               onError={() => setStuk(true)} />
        ) : (
          <div className="geenbeeld">{item.content_type === 'video' ? '▶' : '▤'}</div>
        )}
        {duur ? <span className="duur">{duur}</span> : null}
        <label className="kies" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={gekozen}
                 onChange={(e) => onKies(item.id, e.target.checked)} />
        </label>
      </div>
      <div className="onder">
        <div className="row" style={{ gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
          <Tag kind={item.processing_status === 'processed' ? 'ok' : 'mut'}>
            {item.processing_status === 'processed' ? 'Processed' : 'Unprocessed'}
          </Tag>
          {acties ? <Tag kind="mut">{item.content_type}</Tag> : null}
          {item.intensity ? <Tag kind="mut">{item.intensity}</Tag> : null}
          {acties && item.base_price ? <Tag kind="mut">€{item.base_price}</Tag> : null}
        </div>
        <div className="titel" title={item.title}>{item.title}</div>
        <div className="pad mut2" title={item.folder_path ?? ''}>
          {item.folder_path ?? '—'}
        </div>
        {acties && (item.tags ?? []).length ? (
          <div className="pad mut2" title={(item.tags ?? []).join(', ')}>
            {(item.tags ?? []).slice(0, 3).join(' · ')}
          </div>
        ) : null}
      </div>
      {acties ? (
        <div className="kaartacties">
          <button type="button" title="Preview" onClick={() => onOpen(item)}>👁</button>
          <button type="button" title="Download" onClick={() => acties.download(item)}>⬇</button>
          <button type="button" title="Convert to JPG/MP4"
                  className={MOET_OM.test(item.file_reference ?? '') ? 'let' : ''}
                  onClick={() => acties.converteer(item)}>⇄</button>
          {item.processing_status === 'processed' ? (
            <button type="button" title="Move back to unprocessed"
                    onClick={() => acties.terug(item)}>↺</button>
          ) : (
            <button type="button" title="Process" className="ok"
                    onClick={() => acties.verwerk(item)}>✓</button>
          )}
          <button type="button" title="Edit" onClick={() => acties.bewerk(item)}>✎</button>
          <button type="button" title="Remove from library (the NAS file stays)"
                  className="weg" onClick={() => acties.archiveer(item)}>🗑</button>
        </div>
      ) : null}
    </div>
  )
}

export default function ContentLibrary({ modelId, personaIds, naam, tellers, onTerug }: {
  modelId: number
  personaIds: number[]
  naam: string
  /** De aantallen van de kaart waarop is geklikt. Bewust van de kaart en niet
   *  van `/items/counts`: dat endpoint kent alleen `model_persona_id` en geen
   *  lijst, en gaf bij een samengevoegde bibliotheek het totaal van ALLE
   *  modellen terug — 42.167 in plaats van 5.691. */
  tellers: { total: number; unprocessed: number; processed: number }
  onTerug: () => void
}) {
  const { run, busy, notify } = useActions()
  const mij = useMij()
  // Fase 1 (23-10-2026): de controls van oud alleen voor owner/admin. Rechten
  // voor managers en chatters komen in fase 2; tot dan ziet een manager wat hij
  // al zag.
  const admin = isVolledigBeheer(mij.data)
  // 26-10-2026 (Jordy): de doelvinkjes per map zet alleen de owner.
  const owner = String(mij.data?.role ?? '').toLowerCase() === 'owner'
  const [doelenMap, setDoelenMap] = useState<Map_ | null>(null)
  const [bewerk, setBewerk] = useState<Item | null>(null)
  const [verplaats, setVerplaats] = useState(false)
  const [doelMap, setDoelMap] = useState('')
  const [syncBezig, setSyncBezig] = useState(false)
  // Fase 2 (23-10-2026): downloaden voor wie het recht heeft; de server
  // beslist (N per actie, alleen Processed, 24-uursgrens).
  const rechten = useApi<{ can_download: boolean; max_per_action: number | null;
                           limit_24h: number | null; used_24h: number | null;
                           processed_only: boolean }>('/api/admin/content/download-rights')
  const magDownloaden = admin || !!rechten.data?.can_download
  const maxN = admin ? null : (rechten.data?.max_per_action ?? null)
  const pids = personaIds.length ? personaIds.join(',') : String(modelId)

  const boom = useApi<Map_[]>(`/api/admin/content-library/tree/${modelId}`, [modelId])

  const [map, setMap] = useState<number | null>(null)
  const [status, setStatus] = useState('all')
  const [soort, setSoort] = useState('all')
  const [zoek, setZoek] = useState('')
  const [zoekTraag, setZoekTraag] = useState('')
  const [items, setItems] = useState<Item[]>([])
  const [meer, setMeer] = useState(false)
  const [laadt, setLaadt] = useState(true)
  const [fout, setFout] = useState<unknown>(null)
  const [gekozen, setGekozen] = useState<Set<number>>(new Set())
  const [groot, setGroot] = useState<Item | null>(null)
  const [proef, setProef] = useState<Proef | null>(null)
  const [proefBezig, setProefBezig] = useState(false)
  // Oneindig scrollen (23-10-2026, Jordy): het scrollvak van het raster en een
  // leeg elementje onderaan. `generatie` gooit een late pagina van een vorige
  // filter weg, anders belandt die onder de nieuwe.
  const scrollvak = useRef<HTMLDivElement | null>(null)
  const wacht = useRef<HTMLDivElement | null>(null)
  const generatie = useRef(0)

  // Zoeken pas na een korte stilte: bij elke toetsaanslag de hele bibliotheek
  // opnieuw ophalen maakt het typen schokkerig.
  useEffect(() => {
    const t = window.setTimeout(() => setZoekTraag(zoek.trim()), 350)
    return () => window.clearTimeout(t)
  }, [zoek])

  const query = useCallback((offset: number) => {
    const p = new URLSearchParams({
      persona_ids: pids,
      limit: String(PER_PAGINA),
      offset: String(offset),
    })
    if (map != null) p.set('folder_id', String(map))
    if (status !== 'all') p.set('processing_status', status)
    if (soort !== 'all') p.set('content_type', soort)
    if (zoekTraag) p.set('search', zoekTraag)
    return `/api/admin/content-library/items?${p.toString()}`
  }, [pids, map, status, soort, zoekTraag])

  const haal = useCallback(async (offset: number) => {
    if (offset === 0) {
      generatie.current += 1
      if (scrollvak.current) scrollvak.current.scrollTop = 0
    }
    const mijn = generatie.current
    setLaadt(true)
    setFout(null)
    try {
      const rij = await api.get<Item[]>(query(offset))
      if (mijn !== generatie.current) return
      setItems((oud) => {
        if (offset === 0) return rij
        // Dubbel ophalen (twee keer de rand geraakt) mag geen dubbele kaarten geven.
        const al = new Set(oud.map((i) => i.id))
        return [...oud, ...rij.filter((i) => !al.has(i.id))]
      })
      // Een volle pagina betekent: er is waarschijnlijk meer. De server geeft
      // geen totaal mee op deze route, dus dit is het eerlijke signaal.
      setMeer(rij.length === PER_PAGINA)
    } catch (e) {
      if (mijn === generatie.current) setFout(e)
    } finally {
      if (mijn === generatie.current) setLaadt(false)
    }
  }, [query])

  useEffect(() => { setGekozen(new Set()); haal(0) }, [haal])

  // Bij de onderrand vanzelf de volgende pagina; geen knop meer.
  useBijRand(wacht, scrollvak, () => { if (meer && !laadt) haal(items.length) },
             meer && !laadt && !fout)

  const kies = useCallback((id: number, aan: boolean) => {
    setGekozen((s) => {
      // Downloadrol: nooit meer dan N tegelijk selecteren (de server weigert
      // het ook; dit voorkomt alleen de teleurstelling).
      if (aan && maxN != null && s.size >= maxN && !s.has(id)) {
        notify(`You can download at most ${maxN} item(s) at once.`, false)
        return s
      }
      const n = new Set(s)
      if (aan) n.add(id); else n.delete(id)
      return n
    })
  }, [maxN, notify])

  const t = tellers
  const statusOpties = useMemo(() => ([
    { value: 'all', label: `All (${num(t?.total ?? 0)})` },
    { value: 'unprocessed', label: `Unprocessed (${num(t?.unprocessed ?? 0)})` },
    { value: 'processed', label: `Processed (${num(t?.processed ?? 0)})` },
  ]), [t])

  const bulk = (pad: string, label: string, body: Record<string, unknown>) => run({
    label,
    confirm: `${label} for ${gekozen.size} item(s)?`,
    request: mut.post(pad, { item_ids: [...gekozen], ...body }),
    onDone: () => { setGekozen(new Set()); haal(0); boom.refetch() },
  })

  const mappen = useMemo(() => plat(boom.data ?? []), [boom.data])
  const ververs = useCallback(() => { haal(0); boom.refetch() }, [haal, boom])

  /** Eén pad voor admin en downloadrol: eerst een kortlevend downloadbewijs
   *  (de server toetst alles), dan downloadt de browser zelf naar schijf —
   *  ook een grote ZIP komt dan niet in het geheugen. */
  const download = useCallback(async (ids: number[]) => {
    try {
      const r = await api.post<{ url: string; items: number }>(
        '/api/admin/content/download/ticket', { item_ids: ids })
      const a = document.createElement('a')
      a.href = r.url
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
      notify(ids.length > 1 ? `Preparing a ZIP with ${ids.length} items…` : 'Download started.')
      if (!admin) rechten.refetch()
    } catch (e) {
      notify(`Download refused: ${e instanceof Error ? e.message : e}`, false)
    }
  }, [admin, notify, rechten])

  const zetZichtbaar = useCallback(async (m: Map_, aan: boolean) => {
    const sub = (m.children ?? []).length
    const ookSub = sub > 0 && window.confirm(
      `${aan ? 'Make' : 'Hide'} “${m.name}” ${aan ? 'visible' : ''} for chatters.\n\n`
      + `Apply the same to its ${sub} subfolder(s) (and theirs)? OK = yes, Cancel = only this folder.`)
    try {
      await api.patch(`/api/admin/content-library/folders/${m.id}/chatter-visible`,
                      { chatter_visible: aan, apply_to_children: ookSub })
      notify(aan ? 'Visible and sellable for chatters.' : 'Hidden from chatters.')
      boom.refetch()
    } catch (e) {
      notify(`Could not change visibility: ${e instanceof Error ? e.message : e}`, false)
    }
  }, [boom, notify])

  /** Uit de library halen = archiveren. NOOIT `hard_delete`: dat wist het
   *  bestand op de NAS, en de NAS wordt nooit geleegd. Een gearchiveerd item
   *  verdwijnt uit alle lijsten en komt bij een NAS-sync niet terug. */
  const archiveer = useCallback(async (ids: number[]) => {
    let mis = 0
    for (const id of ids) {
      try { await api.del(`/api/admin/content-library/items/${id}`) } catch { mis += 1 }
    }
    notify(mis ? `${ids.length - mis} removed, ${mis} failed.`
               : `${ids.length} item(s) removed from the library. NAS files untouched.`, !mis)
    setGekozen(new Set())
    ververs()
  }, [notify, ververs])

  const acties = useMemo<KaartActies | undefined>(() => (admin ? {
    download: (it) => { void download([it.id]) },
    converteer: (it) => run({
      label: 'Convert',
      confirm: `Convert "${it.title}" to JPG/MP4? The converted file is added next to the original on the NAS; the original stays (hidden in the library).`,
      request: mut.post(`/api/admin/content-library/items/${it.id}/convert`),
      successText: 'Conversion started. Videos convert in the background.',
      onDone: () => haal(0),
    }),
    verwerk: (it) => setBewerk(it),
    terug: (it) => run({
      label: 'Unprocess',
      request: mut.patch(`/api/admin/content-library/items/${it.id}/unprocess`),
      successText: 'Moved back to unprocessed.',
      onDone: ververs,
    }),
    bewerk: (it) => setBewerk(it),
    archiveer: (it) => {
      if (!window.confirm(`Remove "${it.title}" from the library?\n\n`
        + 'The file on the NAS is NOT deleted; the item is archived and hidden.')) return
      void archiveer([it.id])
    },
  } : undefined), [admin, run, notify, haal, ververs, archiveer, download])

  return (
    <>
      <div className="filters">
        <button className="btn ghost sm" type="button" onClick={onTerug}>
          ← All models
        </button>
        <b style={{ fontSize: 15 }}>{naam}</b>
        <span className="spacer" style={{ flex: 1 }} />

        {/* Fase 2 (23-10-2026): upload, sync en converteren zijn owner/admin
            (server: require_owner_or_admin). */}
        {admin ? (
          <>
        <label className="btn ghost sm"
               style={{ cursor: map == null ? 'not-allowed' : 'pointer', opacity: map == null ? 0.5 : 1 }}
               title={map == null ? 'Choose a folder first; new uploads are hidden from chatters until you tick the folder.' : 'Upload into this folder'}>
          Upload
          <input type="file" multiple style={{ display: 'none' }} disabled={map == null}
                 onChange={async (e) => {
                   const bestanden = [...(e.target.files ?? [])]
                   if (!bestanden.length) return
                   const fd = new FormData()
                   fd.append('model_persona_id', String(modelId))
                   if (map != null) fd.append('folder_id', String(map))
                   for (const f of bestanden) fd.append('files', f)
                   try {
                     await api.postForm('/api/admin/content-library/upload', fd)
                     notify(`${bestanden.length} file(s) uploaded.`)
                     haal(0); boom.refetch()
                   } catch (fout2) {
                     notify(`Upload failed: ${fout2 instanceof Error ? fout2.message : fout2}`,
                            false)
                   }
                 }} />
        </label>

        {/* Alleen de PROEF. De echte `POST /sync-from-nas/{id}` maakt rijen aan
            én archiveert mappen die niet meer op de NAS staan; die knop stond
            hier eerst met `?dry_run=true` erachter, en dat is een parameter die
            het endpoint niet kent — hij had dus gewoon een echte synchronisatie
            gedraaid. */}
        <button className="btn ghost sm" type="button" disabled={proefBezig}
                onClick={async () => {
                  setProefBezig(true)
                  try {
                    setProef(await api.get<Proef>(
                      `/api/admin/content-library/sync-from-nas/${modelId}/preview?limit=40`))
                  } catch (e) {
                    notify(`Sync preview: ${e instanceof Error ? e.message : e}`, false)
                  } finally {
                    setProefBezig(false)
                  }
                }}>
          {proefBezig ? 'Scanning NAS…' : admin ? 'Sync from NAS' : 'Sync from NAS (dry run)'}
        </button>

        <button className="btn ghost sm" type="button" disabled={busy === 'Convert'}
                onClick={() => run({
                  label: 'Convert',
                  confirm: `Convert all media of ${naam} to JPG/MP4?`,
                  request: mut.post('/api/admin/content-library/convert-all',
                                    { model_persona_id: modelId }),
                  onDone: () => haal(0),
                })}>
          Convert all (this model) → JPG/MP4
        </button>
          </>
        ) : null}
      </div>

      <div className="bibliotheek bibliotheek-vol contentpagina">
        {/* ---- mappenboom ---- */}
        <div className="panel mappen">
          <div className="head">
            <h3>Folders</h3>
            {admin ? (
              <span className="mut2" style={{ fontSize: 11.5 }}
                    title="Tick a folder to make it visible and sellable for chatters. New folders start hidden.">
                ☑ = visible for chatters
              </span>
            ) : null}
          </div>
          <div className="body flush">
            <div className="boomscroll">
              <div className={map === null ? 'tak op' : 'tak'} style={{ paddingLeft: 8 }}>
                <span className="klap leeg" />
                <button type="button" className="naam" onClick={() => setMap(null)}>
                  All Content
                </button>
                <span className="mut2 telling">{num(t?.total ?? 0)}</span>
              </div>
              {boom.loading ? <Loading /> : boom.error ? <ErrorNote error={boom.error} />
                : (boom.data ?? []).map((m) => (
                    <Tak key={m.id} map={m} actief={map} onKies={setMap} diep={0}
                         onZichtbaar={admin ? zetZichtbaar : undefined}
                         onDoelen={owner ? setDoelenMap : undefined} />
                  ))}
            </div>
          </div>
        </div>

        {/* ---- werkbalk + grid ---- */}
        <div className="panel media">
          <div className="body">
            <div className="filters" style={{ marginBottom: 0 }}>
              <Seg options={statusOpties} value={status} onChange={setStatus} />
              <Seg options={SOORT} value={soort} onChange={setSoort} />
              <input className="inp" style={{ maxWidth: 220 }} placeholder="Search..."
                     value={zoek} onChange={(e) => setZoek(e.target.value)} />
              <span className="spacer" style={{ flex: 1 }} />
              {gekozen.size ? (
                <>
                  <span className="mut2" style={{ fontSize: 12 }}>
                    {gekozen.size} selected
                  </span>
                  {magDownloaden ? (() => {
                    const keuze = items.filter((i) => gekozen.has(i.id))
                    const teVeel = maxN != null && gekozen.size > maxN
                    const onverwerkt = !admin && keuze.some((i) => i.processing_status !== 'processed')
                    const reden = teVeel ? `At most ${maxN} per download`
                      : onverwerkt ? 'Only processed items can be downloaded' : ''
                    return (
                      <button className="btn ghost sm" type="button" disabled={!!reden}
                              title={reden || (gekozen.size > 1 ? 'Downloads a ZIP' : 'Downloads the file')}
                              onClick={() => void download([...gekozen])}>
                        Download selected{reden ? ` — ${reden}` : ''}
                      </button>
                    )
                  })() : null}
                  {admin ? (
                    <button className="btn ghost sm" type="button"
                            onClick={() => bulk('/api/admin/content-library/items/bulk-process',
                                                'Mark processed', {})}>
                      Mark processed
                    </button>
                  ) : null}
                  {admin ? (
                    <>
                      <button className="btn ghost sm" type="button"
                              onClick={() => { setDoelMap(''); setVerplaats(true) }}>
                        Move to folder
                      </button>
                      <button className="btn danger sm" type="button"
                              onClick={() => {
                                if (!window.confirm(`Remove ${gekozen.size} item(s) from the library?\n\n`
                                  + 'The files on the NAS are NOT deleted; the items are archived and hidden.')) return
                                void archiveer([...gekozen])
                              }}>
                        Remove from library
                      </button>
                    </>
                  ) : null}
                  <button className="btn ghost sm" type="button"
                          onClick={() => setGekozen(new Set())}>
                    Clear
                  </button>
                </>
              ) : (
                <span className="mut2" style={{ fontSize: 12 }}>
                  {num(items.length)} shown
                  {!admin && rechten.data?.can_download && rechten.data.limit_24h != null
                    ? ` · downloads today ${num(rechten.data.used_24h ?? 0)}/${num(rechten.data.limit_24h)}`
                    : ''}
                </span>
              )}
            </div>
          </div>
          <div className="body flush" ref={scrollvak}>
            <div className="mediagrid">
              {items.map((it) => (
                <Kaart key={it.id} item={it} gekozen={gekozen.has(it.id)}
                       onKies={kies} onOpen={setGroot} scrollvak={scrollvak}
                       acties={acties} />
              ))}
              <div className="wachter" ref={wacht} />
            </div>
            {fout ? <ErrorNote error={fout} /> : null}
            {fout && items.length ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
                <button className="btn ghost sm" type="button"
                        onClick={() => haal(items.length)}>
                  Retry
                </button>
              </div>
            ) : null}
            {laadt ? <Loading /> : null}
            {!laadt && items.length === 0 ? (
              <div className="feedrow mut2" style={{ justifyContent: 'center' }}>
                No media in this folder.
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* ---- uitkomst van de proefsynchronisatie ---- */}
      {proef ? (
        <Modal
          title={`Sync from NAS — ${proef.model}`}
          desc="What a sync would change. Nothing has been changed."
          width={620}
          onClose={() => setProef(null)}
          footer={
            <>
              <button className="btn ghost sm" type="button"
                      onClick={() => setProef(null)}>Close</button>
              {/* Echte sync alleen voor owner/admin en alleen na deze preview.
                  Hij voegt nieuwe NAS-bestanden toe en archiveert rijen
                  waarvan het bestand weg is; hij wist zelf nooit bestanden. */}
              <button className="btn primary sm" type="button"
                      disabled={!admin || syncBezig || !proef.nas_root}
                      onClick={async () => {
                        if (!window.confirm(`Sync ${proef.model} with the NAS now?\n\n`
                          + 'New files are added; rows whose file is gone are archived. '
                          + 'No file on the NAS is deleted.')) return
                        setSyncBezig(true)
                        try {
                          await api.post(`/api/admin/content-library/sync-from-nas/${modelId}`)
                          notify('Sync from NAS done.')
                          setProef(null)
                          ververs()
                        } catch (e) {
                          notify(`Sync failed: ${e instanceof Error ? e.message : e}`, false)
                        } finally {
                          setSyncBezig(false)
                        }
                      }}>
                {syncBezig ? 'Syncing…' : 'Apply'}
              </button>
            </>
          }
        >
          {proef.nas_root ? (
            <>
              <div className="kv" style={{ marginBottom: 10 }}>
                <span>On the NAS</span><span className="mono">{num(proef.on_nas)}</span>
                <span>In the index</span><span className="mono">{num(proef.in_index)}</span>
                <span>New on the NAS</span>
                <span className="mono">{num(proef.new_on_nas_total ?? 0)}</span>
                <span>No longer on the NAS</span>
                <span className="mono">{num(proef.missing_on_nas_total ?? 0)}</span>
              </div>
              {proef.new_on_nas.length ? (
                <>
                  <b style={{ fontSize: 12.5 }}>Would be added</b>
                  <ul className="paden">
                    {proef.new_on_nas.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                </>
              ) : null}
              {proef.missing_on_nas.length ? (
                <>
                  <b style={{ fontSize: 12.5 }}>No longer on the NAS</b>
                  <ul className="paden">
                    {proef.missing_on_nas.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                </>
              ) : null}
              <div className="note" style={{ marginTop: 12 }}>
                {admin ? (
                  <><b>Apply</b> adds the new files and archives the rows whose file is
                  gone. It never deletes a file on the NAS.</>
                ) : (
                  <><b>Apply is off.</b> Only an owner or admin can run the real sync.</>
                )}
              </div>
            </>
          ) : (
            <div className="note">{proef.message}</div>
          )}
        </Modal>
      ) : null}

      {bewerk ? (
        <ContentBewerken item={bewerk} mappen={mappen} onClose={() => setBewerk(null)}
                         onKlaar={(m) => { setBewerk(null); notify(m); ververs() }} />
      ) : null}

      {verplaats ? (
        <Modal title="Move to folder" desc={`${gekozen.size} item(s)`} width={460}
               onClose={() => setVerplaats(false)}
               footer={
                 <>
                   <button className="btn ghost sm" type="button"
                           onClick={() => setVerplaats(false)}>Cancel</button>
                   <button className="btn primary sm" type="button" disabled={!doelMap}
                           onClick={() => {
                             setVerplaats(false)
                             bulk('/api/admin/content-library/items/bulk-move', 'Move',
                                  { folder_id: Number(doelMap) })
                           }}>Move</button>
                 </>
               }>
          <select className="inp" value={doelMap} onChange={(e) => setDoelMap(e.target.value)}>
            <option value="">Choose a folder…</option>
            {mappen.map((m) => <option key={m.id} value={String(m.id)}>{m.pad}</option>)}
          </select>
        </Modal>
      ) : null}

      {/* ---- vergrote weergave ---- */}
      {groot ? (
        <div className="lichtbak" onClick={() => setGroot(null)}>
          <div className="inhoud" onClick={(e) => e.stopPropagation()}>
            {groot.content_type === 'video' ? (
              <video src={fileUrl(groot.id)} controls autoPlay />
            ) : (
              <img src={fileUrl(groot.id)} alt={groot.title} />
            )}
            <div className="bijschrift">
              <b>{groot.title}</b>
              <span className="mut2">{groot.folder_path}</span>
              {acties ? (
                <>
                  <a className="btn ghost sm" href={fileUrl(groot.id)} target="_blank"
                     rel="noreferrer">Open in new tab</a>
                  <button className="btn ghost sm" type="button"
                          onClick={() => acties.download(groot)}>Download</button>
                </>
              ) : null}
              <button className="btn ghost sm" type="button"
                      onClick={() => setGroot(null)}>Close</button>
            </div>
          </div>
        </div>
      ) : null}
      {doelenMap ? (
        <MapDoelen map={doelenMap} onClose={() => setDoelenMap(null)}
                   onSaved={(tekst) => { notify(tekst); boom.refetch() }}
                   onFout={(tekst) => notify(tekst, false)} />
      ) : null}
    </>
  )
}

/**
 * 26-10-2026 (Jordy): waarvoor een map gebruikt mag worden. Eén bron: de vier
 * vinkjes staan op de map zelf. Geen erfenis bij het lezen; "Apply to all
 * subfolders" zet dezelfde waarden expliciet op alle onderliggende mappen.
 * Een nieuwe map staat overal uit; een nieuwe submap neemt alleen een doel over
 * als bij deze map precies één doel aan staat (besluit Jordy 26-10).
 */
function MapDoelen({ map, onClose, onSaved, onFout }: {
  map: Map_
  onClose: () => void
  onSaved: (tekst: string) => void
  onFout: (tekst: string) => void
}) {
  const [doelen, setDoelen] = useState({
    chatters: !!map.chatter_visible,
    posting_bot: !!map.posting_bot_visible,
    paid_bot: !!map.paid_bot_visible,
    social: !!map.social_visible,
  })
  const [ookSub, setOokSub] = useState(false)
  const [bezig, setBezig] = useState(false)
  const sub = (map.children ?? []).length
  const regels: [keyof typeof doelen, string, string][] = [
    ['chatters', 'Chatters', 'Visible and sellable for chatters in the chats.'],
    ['posting_bot', 'Posting bot', 'The free posting bot may use this folder.'],
    ['paid_bot', 'Paid posting bot', 'The paid posting bot may use this folder.'],
    ['social', 'Social media', 'Instagram, Threads and X may use this folder.'],
  ]
  async function opslaan() {
    setBezig(true)
    try {
      await api.patch(`/api/admin/content-library/folders/${map.id}/purposes`,
                      { ...doelen, apply_to_children: ookSub })
      onSaved(ookSub ? `Saved for “${map.name}” and all its subfolders.` : `Saved for “${map.name}”.`)
      onClose()
    } catch (e) {
      onFout(`Could not save: ${e instanceof Error ? e.message : e}`)
    } finally {
      setBezig(false)
    }
  }
  return (
    <Modal title={`Folder purposes — ${map.name}`} width={460} onClose={onClose}
           desc="What this folder may be used for. Each folder has its own settings. A new folder starts with everything off; a new subfolder only takes over a purpose when exactly one purpose is on for this folder."
           footer={
             <>
               <button className="btn ghost sm" type="button" onClick={onClose}>Cancel</button>
               <button className="btn primary sm" type="button" disabled={bezig} onClick={() => void opslaan()}>Save</button>
             </>
           }>
      <div style={{ display: 'grid', gap: 8 }}>
        {regels.map(([sleutel, label, uitleg]) => (
          <label key={sleutel} className="row" style={{ gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
            <input type="checkbox" checked={doelen[sleutel]}
                   onChange={(e) => setDoelen((d) => ({ ...d, [sleutel]: e.target.checked }))} />
            <span><b>{label}</b><br /><span className="mut2" style={{ fontSize: 12 }}>{uitleg}</span></span>
          </label>
        ))}
        {sub ? (
          <label className="row" style={{ gap: 8, fontSize: 13, marginTop: 6 }}>
            <input type="checkbox" checked={ookSub} onChange={(e) => setOokSub(e.target.checked)} />
            Apply to all subfolders ({sub} directly below, and theirs)
          </label>
        ) : null}
      </div>
    </Modal>
  )
}
